import { AuthGuard } from '@auth0/auth0-angular';

import { AssetGuard } from '../features/asset/service/asset.guard';
import { viewPageNoMetaResolveFn } from '../features/deal/services/view-entity.resolver';
import { ActivityEvent } from '../features/home/models/activity-event.model';
import { onboardingSetupGuard } from '../features/setup/onboarding-setup.guard';
import { SemerisRoutes } from '../features/shared/constants/routes';
import { EMBEDDED_SEGMENT, WORKSPACE_SCOPED_SEGMENTS, WORKSPACE_URL_NAMESPACE } from '../features/shared/constants/workspace-scoped-routes';
import { Permission, PermissionRelation } from '../features/shared/models/api/permissions.enum';
import { accountStatusGuard, blockedGuard, welcomeGuard } from '../features/shared/services/account-status.guard';
import { EmbeddedGuard } from '../features/shared/services/embedded.guard';
import { PermissionGuard } from '../features/shared/services/permission.guard';
import { workspaceToolGuard } from '../features/shared/services/workspace-tool.guard';
import { unprefixedWorkspaceUrlGuard, workspaceUrlGuard } from '../features/shared/services/workspace-url.guard';
import { workspaceViewActivityResolveFn } from '../features/workspace-ui/workspace-activity.resolver';
import { workspaceRecordResolver } from '../features/workspace-ui/workspace-record.resolver';
import type { AppRoutes } from '../types/route-data';

/**
 * Appends `workspaceToolGuard` to every navigable top-level route — `redirectTo` routes and `**` are
 * skipped. `canActivateChild` too: a reused route node skips its own `canActivate`, so a nav between
 * two children of one route (`/admin/users` → `/admin/catalog`) would otherwise go unchecked.
 */
function withWorkspaceToolGuard(routes: AppRoutes): AppRoutes {
  return routes.map((route) =>
    route.redirectTo || route.path === '**'
      ? route
      : {
          ...route,
          canActivate: [...(route.canActivate ?? []), workspaceToolGuard],
          canActivateChild: [...(route.canActivateChild ?? []), workspaceToolGuard],
        },
  );
}

/**
 * Whether a route declaration belongs under `/ws/:workspaceId`, matched on its first path segment
 * against {@link WORKSPACE_SCOPED_SEGMENTS}.
 *
 * The embedded deal view is the one exception: it lives under a scoped first segment but its URL is
 * an external contract that the Excel add-in and third-party iframes construct.
 */
function isWorkspaceScopedRoute(path: string | undefined): boolean {
  const segments = (path ?? '').split('/').filter(Boolean);

  return !segments.includes(EMBEDDED_SEGMENT) && WORKSPACE_SCOPED_SEGMENTS.includes(segments[0] ?? '');
}

/**
 * Moves the workspace-scoped routes under a `/ws/:workspaceId` parent, leaving tenant-scoped and
 * bootstrap routes at the root.
 *
 * Done here rather than by hand so the split can never drift from the registry the guards read.
 * `**` stays last — it is where an un-prefixed scoped URL now lands, and
 * {@link unprefixedWorkspaceUrlGuard} canonicalises it from there.
 */
function nestWorkspaceRoutes(routes: AppRoutes): AppRoutes {
  const flat: AppRoutes = [];
  const scoped: AppRoutes = [];
  const notFound: AppRoutes = [];

  for (const route of routes) {
    if (route.path === '**') {
      notFound.push({ ...route, canActivate: [...(route.canActivate ?? []), unprefixedWorkspaceUrlGuard] });
    } else if (isWorkspaceScopedRoute(route.path)) {
      scoped.push(route);
    } else {
      flat.push(route);
    }
  }

  return [...flat, { path: `${WORKSPACE_URL_NAMESPACE}/:workspaceId`, canActivate: [workspaceUrlGuard], children: scoped }, ...notFound];
}

/** First segments an account without access can still open: its status pages and auth. */
const ACCOUNT_STATUS_EXEMPT_SEGMENTS: readonly string[] = [SemerisRoutes.auth.root, SemerisRoutes.welcome, SemerisRoutes.blocked];

/**
 * Puts `accountStatusGuard` first on every top-level route bar the exempt ones, `/ws/:workspaceId` and `**` included.
 * First, so its redirect wins over later guards waiting on an init that accounts without access never finish.
 */
function withAccountStatusGuard(routes: AppRoutes): AppRoutes {
  return routes.map((route) =>
    route.redirectTo || ACCOUNT_STATUS_EXEMPT_SEGMENTS.includes((route.path ?? '').split('/')[0])
      ? route
      : { ...route, canActivate: [accountStatusGuard, ...(route.canActivate ?? [])] },
  );
}

const declaredRoutes: AppRoutes = withWorkspaceToolGuard([
  {
    path: SemerisRoutes.home.root,
    loadChildren: () => import('../features/home/routes').then((m) => m.homeRoutes),
    data: {
      breadcrumb: 'Home',
    },
    canActivate: [AuthGuard, AssetGuard, PermissionGuard],
  },
  {
    path: SemerisRoutes.admin.root,
    loadChildren: () => import('../features/admin/routes').then((m) => m.adminRoutes),
    // No `breadcrumb` — `/admin` has no page of its own, and a labelled crumb would link to it.
    canActivate: [AuthGuard, PermissionGuard],
  },
  {
    path: SemerisRoutes.asset.changeAsset,
    loadChildren: () => import('../features/asset/routes').then((m) => m.assetRoutes),
    canActivate: [AuthGuard],
  },
  {
    path: SemerisRoutes.auth.root,
    loadChildren: () => import('../features/status-pages/auth.routes').then((m) => m.authRoutes),
    canActivate: [],
  },
  {
    path: SemerisRoutes.integrations.root,
    loadChildren: () => import('../features/integration/routes').then((m) => m.integrationRoutes),
    canActivate: [AuthGuard, PermissionGuard],
  },
  {
    path: SemerisRoutes.analysisAccelerator.root,
    loadChildren: () => import('../features/analysis-accelerator/routes').then((m) => m.analysisAcceleratorRoutes),
    canActivate: [AuthGuard, PermissionGuard],
  },
  {
    path: SemerisRoutes.settings.root,
    loadChildren: () => import('../features/settings/routes').then((m) => m.settingsRoutes),
    data: {
      breadcrumb: 'Settings',
    },
    canActivate: [AuthGuard],
  },
  {
    // Tenant-scoped and ungated beyond auth and account status: the User Guide also serves
    // onboarding users, who have no resolved workspace and few permissions.
    path: SemerisRoutes.help.root,
    data: {
      breadcrumb: 'User Guide',
    },
    canActivate: [AuthGuard],
    children: [
      {
        path: '**',
        loadComponent: () => import('../features/help/containers/help-page/help-page.component').then((m) => m.HelpPageComponent),
      },
    ],
  },
  {
    path: 'all-tools',
    loadComponent: () => import('../features/all-tools/all-tools.component').then((m) => m.AllToolsComponent),
    data: { breadcrumb: 'All Tools' },
    canActivate: [AuthGuard],
  },
  {
    path: 'benchmark/json-editor',
    loadComponent: () =>
      import('../features/json-editor/containers/json-editor-benchmark/json-editor-benchmark.component').then(
        (m) => m.JsonEditorBenchmarkComponent,
      ),
    canActivate: [AuthGuard],
  },
  {
    path: SemerisRoutes.unauthorized,
    loadComponent: () => import('../features/status-pages/unauthorized.component').then((m) => m.UnauthorizedComponent),
    canActivate: [AuthGuard],
  },
  {
    path: SemerisRoutes.welcome,
    loadComponent: () => import('../features/status-pages/welcome.component').then((m) => m.WelcomeComponent),
    data: { fullscreen: true },
    canActivate: [AuthGuard, welcomeGuard],
  },
  {
    path: SemerisRoutes.setup,
    loadComponent: () => import('../features/setup/containers/setup-wizard/setup-wizard.component').then((m) => m.SetupWizardComponent),
    data: { fullscreen: true },
    // No AssetGuard (so the setup-gate can't loop) and no SetupGuard (so the wizard stays
    // reachable after completion, e.g. from the account menu).
    canActivate: [AuthGuard, onboardingSetupGuard],
  },
  {
    path: SemerisRoutes.selectWorkspace,
    loadComponent: () =>
      import('../features/setup/containers/select-workspace/select-workspace.component').then((m) => m.SelectWorkspaceComponent),
    data: { breadcrumb: 'Workspaces' },
    // AuthGuard only — AssetGuard redirects HERE for a stale workspace, so guarding this route
    // with it would loop.
    canActivate: [AuthGuard],
  },
  {
    path: SemerisRoutes.blocked,
    loadComponent: () => import('../features/status-pages/blocked.component').then((m) => m.BlockedComponent),
    data: { fullscreen: true },
    canActivate: [AuthGuard, blockedGuard],
  },
  {
    path: SemerisRoutes.formula.root,
    loadChildren: () => import('../features/formula/routes').then((m) => m.formulaRoutes),
    data: {
      breadcrumb: 'Formulas',
    },
    canActivate: [AuthGuard, AssetGuard, PermissionGuard],
  },
  {
    path: SemerisRoutes.datapoint.root,
    loadChildren: () => import('../features/unified-datapoint/routes').then((m) => m.unifiedDatapointRoutes),
    data: {
      breadcrumb: 'Datapoints',
    },
    canActivate: [AuthGuard, AssetGuard, PermissionGuard],
  },
  {
    path: `${SemerisRoutes.transactions.root}/${SemerisRoutes.transactions.getByParametersEmbedded}`,
    loadChildren: () => import('../features/embedded/routes').then((m) => m.embeddedRoutes),
    canActivate: [EmbeddedGuard, AssetGuard],
  },
  {
    path: SemerisRoutes.documents.root,
    loadChildren: () => import('../features/document/routes').then((m) => m.documentRoutes),
    data: {
      breadcrumb: 'Documents',
    },
    canActivate: [AuthGuard, AssetGuard, PermissionGuard],
  },
  {
    path: SemerisRoutes.search.documentSearch,
    loadChildren: () => import('../features/document-search/routes').then((m) => m.documentSearchRoutes),
    canActivate: [AuthGuard, AssetGuard, PermissionGuard],
  },

  {
    path: SemerisRoutes.managers.root,
    loadChildren: () => import('../features/manager/routes').then((m) => m.managerRoutes),
    data: {
      breadcrumb: 'CLO Managers',
    },
    canActivate: [AuthGuard, AssetGuard, PermissionGuard],
  },
  {
    path: SemerisRoutes.transactions.root,
    loadChildren: () => import('../features/deal/routes').then((m) => m.dealRoutes),
    canActivate: [AuthGuard, AssetGuard, PermissionGuard],
  },
  {
    path: SemerisRoutes.dealFilter.root,
    loadChildren: () => import('../features/deal-filter/routes').then((m) => m.dealFilterRoutes),
    canActivate: [AuthGuard, AssetGuard, PermissionGuard],
  },
  {
    path: SemerisRoutes.marketComparison.root,
    loadChildren: () => import('../features/market-comparison/routes').then((m) => m.marketComparisonRoutes),
    canActivate: [AuthGuard, AssetGuard, PermissionGuard],
  },
  {
    path: SemerisRoutes.identifiers.root,
    loadChildren: () => import('../features/identifier/routes').then((m) => m.identifierRoutes),
    canActivate: [AuthGuard, AssetGuard, PermissionGuard],
  },
  {
    path: SemerisRoutes.topic.root,
    loadChildren: () => import('../features/topic/routes').then((m) => m.topicRoutes),
    canActivate: [AuthGuard, AssetGuard, PermissionGuard],
  },
  // Backward-compat redirect: old singular `/topic` → new `/topics`. Drop
  // after one release. Hardcoded destination on purpose — if anyone ever
  // flips `SemerisRoutes.topic.root` back to `'topic'` the dynamic
  // version would self-loop.
  {
    path: 'topic',
    redirectTo: 'topics',
    pathMatch: 'prefix',
  },
  {
    path: SemerisRoutes.checklist.root,
    loadChildren: () => import('../features/checklist/routes').then((m) => m.checklistRoutes),
    data: {
      breadcrumb: 'Checklists',
    },
    canActivate: [AuthGuard, AssetGuard, PermissionGuard],
  },
  {
    path: SemerisRoutes.labels,
    loadComponent: () =>
      import('../features/label/containers/label-list-container/label-list-container.component').then((m) => m.LabelListContainerComponent),
    data: {
      breadcrumb: 'Labels',
      permissions: [Permission.READ_LABEL],
      permissionsRelation: PermissionRelation.AND,
    },
    canActivate: [AuthGuard, AssetGuard, PermissionGuard],
  },
  {
    path: SemerisRoutes.charts,
    // Replaces the hand-ported local screen. Per-screen entitlement is the workspace's own — see
    // the `/diagrams` note below; `PermissionGuard` only enforces the group-wide `read:workspace`.
    loadChildren: () => import('@semeris/workspace-ui/charts').then((m) => m.CHARTS_ROUTES),
    data: {
      activityEventType: ActivityEvent.WorkspaceChartsView,
      breadcrumb: 'Charts',
      permissions: [Permission.READ_WORKSPACE],
      permissionsRelation: PermissionRelation.AND,
    },
    canActivate: [AuthGuard, AssetGuard, PermissionGuard],
    resolve: { workspaceRecord: workspaceRecordResolver, activity: workspaceViewActivityResolveFn },
  },
  {
    path: SemerisRoutes.diagrams,
    // The one Workspace screen with a permission of its own: `read:docstore-diagrams` is ungranted,
    // which is what takes Diagrams out of the product. `DIAGRAMS_GATE` still decides whether a
    // workspace offers the screen at all.
    loadChildren: () => import('@semeris/workspace-ui/diagrams').then((m) => m.DIAGRAMS_ROUTES),
    data: {
      activityEventType: ActivityEvent.WorkspaceDiagramsView,
      breadcrumb: 'Diagrams',
      permissions: [Permission.READ_WORKSPACE, Permission.READ_DOCSTORE_DIAGRAMS],
      permissionsRelation: PermissionRelation.AND,
    },
    canActivate: [AuthGuard, AssetGuard, PermissionGuard],
    resolve: { workspaceRecord: workspaceRecordResolver, activity: workspaceViewActivityResolveFn },
  },
  {
    path: SemerisRoutes.datasets,
    loadChildren: () => import('@semeris/workspace-ui/datasets').then((m) => m.DATASETS_ROUTES),
    data: {
      activityEventType: ActivityEvent.WorkspaceDatasetsView,
      breadcrumb: 'Datasets',
      permissions: [Permission.READ_WORKSPACE],
      permissionsRelation: PermissionRelation.AND,
    },
    canActivate: [AuthGuard, AssetGuard, PermissionGuard],
    resolve: { workspaceRecord: workspaceRecordResolver, activity: workspaceViewActivityResolveFn },
  },
  {
    path: SemerisRoutes.datasetExplorer,
    loadChildren: () => import('@semeris/workspace-ui/explorer').then((m) => m.EXPLORER_ROUTES),
    data: {
      activityEventType: ActivityEvent.WorkspaceDatasetExplorerView,
      breadcrumb: 'Dataset Explorer',
      permissions: [Permission.READ_WORKSPACE],
      permissionsRelation: PermissionRelation.AND,
    },
    canActivate: [AuthGuard, AssetGuard, PermissionGuard],
    resolve: { workspaceRecord: workspaceRecordResolver, activity: workspaceViewActivityResolveFn },
  },
  {
    path: SemerisRoutes.workspaceSearch,
    // A new page, not a replacement: the legacy Text Search at `SemerisRoutes.search` is untouched.
    loadChildren: () => import('@semeris/workspace-ui/search').then((m) => m.SEARCH_ROUTES),
    data: {
      activityEventType: ActivityEvent.WorkspaceSearchView,
      breadcrumb: 'Search',
      permissions: [Permission.READ_WORKSPACE],
      permissionsRelation: PermissionRelation.AND,
    },
    canActivate: [AuthGuard, AssetGuard, PermissionGuard],
    resolve: { workspaceRecord: workspaceRecordResolver, activity: workspaceViewActivityResolveFn },
  },
  {
    path: SemerisRoutes.workspaceDocuments,
    // A new page, not a replacement: the legacy `/documents` screen and `/documents/create`
    // are untouched. `PermissionGuard` carries only `read:workspace` — see the `/diagrams` note above.
    loadChildren: () => import('@semeris/workspace-ui/documents').then((m) => m.DOCUMENTS_ROUTES),
    data: {
      activityEventType: ActivityEvent.WorkspaceDocumentsView,
      breadcrumb: 'Documents',
      permissions: [Permission.READ_WORKSPACE],
      permissionsRelation: PermissionRelation.AND,
    },
    canActivate: [AuthGuard, AssetGuard, PermissionGuard],
    resolve: { workspaceRecord: workspaceRecordResolver, activity: workspaceViewActivityResolveFn },
  },
  {
    // Report *documents* — scheduled, AI-generated deliverables. Unrelated to what sdocs used to
    // call a Report, which the gateway renamed Sheet and which now lives at `SemerisRoutes.sheets`.
    path: SemerisRoutes.reports,
    loadChildren: () => import('@semeris/workspace-ui/reports').then((m) => m.REPORTS_ROUTES),
    data: {
      activityEventType: ActivityEvent.WorkspaceReportsView,
      breadcrumb: 'Reports',
      permissions: [Permission.READ_WORKSPACE],
      permissionsRelation: PermissionRelation.AND,
    },
    canActivate: [AuthGuard, AssetGuard, PermissionGuard],
    resolve: { workspaceRecord: workspaceRecordResolver, activity: workspaceViewActivityResolveFn },
  },
  {
    // Labs. No `resolve: { workspaceRecord }` — the screen reads no workspace record, and the
    // resolver would make it wait on a fetch it never uses.
    path: `${SemerisRoutes.labs.root}/${SemerisRoutes.labs.quickView}`,
    loadChildren: () => import('@semeris/workspace-ui/quick-view').then((m) => m.QUICK_VIEW_ROUTES),
    data: {
      activityEventType: ActivityEvent.WorkspaceQuickViewView,
      breadcrumb: 'Quick View',
      permissions: [Permission.READ_LABS],
      permissionsRelation: PermissionRelation.AND,
    },
    canActivate: [AuthGuard, AssetGuard, PermissionGuard],
    resolve: { activity: workspaceViewActivityResolveFn },
  },
  {
    path: SemerisRoutes.sheets,
    loadChildren: () => import('@semeris/workspace-ui/sheets').then((m) => m.SHEETS_ROUTES),
    data: {
      activityEventType: ActivityEvent.WorkspaceSheetsView,
      breadcrumb: 'Sheets',
      permissions: [Permission.READ_WORKSPACE],
      permissionsRelation: PermissionRelation.AND,
    },
    canActivate: [AuthGuard, AssetGuard, PermissionGuard],
    resolve: { workspaceRecord: workspaceRecordResolver, activity: workspaceViewActivityResolveFn },
  },
  {
    path: SemerisRoutes.billing,
    loadComponent: () =>
      import('../features/billing/containers/billing-list-container/billing-list-container.component').then(
        (m) => m.BillingListContainerComponent,
      ),
    data: {
      breadcrumb: 'Billing',
      permissions: [Permission.READ_BILLING, Permission.READ_BILLING_ADMIN],
      permissionsRelation: PermissionRelation.OR,
    },
    canActivate: [AuthGuard, AssetGuard, PermissionGuard],
  },
  {
    path: SemerisRoutes.publication,
    loadComponent: () =>
      import('../features/publication/containers/publication-list-container/publication-list-container.component').then(
        (m) => m.PublicationListContainerComponent,
      ),
    data: {
      breadcrumb: 'Publish Queue',
      permissions: [Permission.READ_PUBLICATION],
      permissionsRelation: PermissionRelation.OR,
    },
    canActivate: [AuthGuard, AssetGuard, PermissionGuard],
  },
  {
    path: SemerisRoutes.newsFeed,
    loadComponent: () =>
      import('../features/news-feed/containers/news-feed-container/news-feed-container.component').then(
        (m) => m.NewsFeedContainerComponent,
      ),
    resolve: { activity: viewPageNoMetaResolveFn },
    data: {
      breadcrumb: 'News Scanner',
      permissions: [Permission.READ_NEWS_CRAWL],
      permissionsRelation: PermissionRelation.AND,
      activityEventType: ActivityEvent.NewsScannerView,
    },
    canActivate: [AuthGuard, AssetGuard, PermissionGuard],
  },
  {
    path: SemerisRoutes.agents,
    // The package's own orchestrated screen, not a bare `<zing-chat>`: it brings the welcome
    // flavors, the artifact dock and the chart/sheet/diagram/report previews with it. Children
    // carry `:agentId`, so a conversation is addressable at `/agents/<id>`.
    loadChildren: () => import('@semeris/workspace-ui/assistant').then((m) => m.ASSISTANT_ROUTES),
    data: {
      activityEventType: ActivityEvent.WorkspaceAgentsView,
      breadcrumb: 'Agents',
      permissions: [Permission.READ_WORKSPACE, Permission.READ_WORKSPACE_AI],
      permissionsRelation: PermissionRelation.AND,
    },
    canActivate: [AuthGuard, AssetGuard, PermissionGuard],
    resolve: { workspaceRecord: workspaceRecordResolver, activity: workspaceViewActivityResolveFn },
  },
  {
    path: SemerisRoutes.notes,
    loadComponent: () =>
      import('../features/comment/containers/conversation-list-container/conversation-list-container.component').then(
        (m) => m.ConversationListContainerComponent,
      ),
    data: {
      breadcrumb: 'Notes',
      permissions: [Permission.READ_COMMENT],
      permissionsRelation: PermissionRelation.AND,
    },
    canActivate: [AuthGuard, AssetGuard, PermissionGuard],
  },
  {
    path: SemerisRoutes.tickler,
    loadComponent: () =>
      import('../features/timeline/containers/tickler-container/tickler-container.component').then((m) => m.TicklerContainerComponent),
    data: {
      breadcrumb: 'Deal Events',
      permissions: [Permission.READ_TICKLER],
      permissionsRelation: PermissionRelation.AND,
    },
    canActivate: [AuthGuard, AssetGuard, PermissionGuard],
  },
  {
    path: SemerisRoutes.datapointOrganizer.root,
    loadComponent: () =>
      import('../features/datapoint-organizer/containers/datapoint-organizer-container/datapoint-organizer-container.component').then(
        (m) => m.DatapointOrganizerContainerComponent,
      ),
    data: {
      breadcrumb: 'Datapoint Organizer',
      permissions: [Permission.READ_DATAPOINT_ORGANIZER],
      permissionsRelation: PermissionRelation.AND,
    },
    canActivate: [AuthGuard, AssetGuard, PermissionGuard],
  },
  {
    path: SemerisRoutes.termSynonymOrganizer.root,
    loadComponent: () =>
      import('../features/term-synonym-organizer/containers/term-synonym-organizer-container/term-synonym-organizer-container.component').then(
        (m) => m.TermSynonymOrganizerContainerComponent,
      ),
    data: {
      breadcrumb: 'Term Synonym Organizer',
      permissions: [Permission.READ_TERM_SYNONYM_ORGANIZER],
      permissionsRelation: PermissionRelation.AND,
    },
    canActivate: [AuthGuard, AssetGuard, PermissionGuard],
  },
  {
    path: SemerisRoutes.positionList.root,
    loadChildren: () => import('../features/position-list/routes').then((m) => m.positionListRoutes),
    data: {
      breadcrumb: 'Stip Lists',
    },
    // Parent guards run first, so without this `workspaceToolGuard` waits on an init that a
    // logged-out session never starts.
    canActivate: [AuthGuard],
  },
  {
    path: SemerisRoutes.aiChat,
    loadComponent: () => import('../features/ai-chat/containers/ai-chat-page/ai-chat-page.component').then((m) => m.AiChatPageComponent),
    data: {
      permissions: [Permission.WRITE_AI_CHAT],
      permissionsRelation: PermissionRelation.AND,
      breadcrumb: 'AI Chat',
    },
    canActivate: [AuthGuard, PermissionGuard],
  },
  {
    path: SemerisRoutes.analystCorner.root,
    children: [
      {
        path: SemerisRoutes.analystCorner.create,
        loadComponent: () =>
          import('../features/analyst-corner/containers/analyst-corner-create/analyst-corner-create.component').then(
            (m) => m.AnalystCornerCreateComponent,
          ),
      },
      {
        path: `${SemerisRoutes.analystCorner.edit.root}/${SemerisRoutes.analystCorner.edit.byId}`,
        loadComponent: () =>
          import('../features/analyst-corner/containers/analyst-corner-create/analyst-corner-create.component').then(
            (m) => m.AnalystCornerCreateComponent,
          ),
      },
      {
        path: SemerisRoutes.analystCorner.byId,
        loadComponent: () =>
          import('../features/analyst-corner/containers/analyst-corner-container/analyst-corner-container.component').then(
            (m) => m.AnalystCornerContainerComponent,
          ),
      },
      {
        path: '',
        loadComponent: () =>
          import('../features/analyst-corner/containers/analyst-corner-container/analyst-corner-container.component').then(
            (m) => m.AnalystCornerContainerComponent,
          ),
      },
    ],
    data: {
      breadcrumb: 'Analyst Corner',
      permissions: [Permission.READ_ANALYST_CORNER],
      permissionsRelation: PermissionRelation.AND,
    },
    canActivate: [AuthGuard, PermissionGuard],
  },
  {
    path: '**',
    loadComponent: () => import('../features/shared/components/not-found/not-found.component').then((m) => m.NotFoundComponent),
    canActivate: [AuthGuard],
  },
]);

export const appRoutes: AppRoutes = withAccountStatusGuard(nestWorkspaceRoutes(declaredRoutes));
